'use client';

/**
 * Vectorbestanden in de browser klaarmaken voor de ontwerpmodule.
 *
 *  - PDF-compatibele .ai: is een PDF, gaat de bestaande PDF-import in.
 *  - SVG: gaat de bestaande afbeeldingsimport in (de editor kan SVG al).
 *  - EPS / PS / oude AI: eerst uploaden (het origineel is wat de drukker
 *    uiteindelijk nodig heeft), dan /api/artwork/convert vragen om een PDF.
 *    Lukt omzetten niet, dan krijgt de klant een voorbeeld om te plaatsen en
 *    zet een mens het origineel om voor het drukken. Nooit een blokkade.
 */

import { prepareArtworkFile, uploadFileToStorage, type RegisteredUpload } from '@/lib/uploads/client-upload';
import { POSTSCRIPT_MIME, type ArtworkDetection } from '@/lib/uploads/artwork-formats';
import type { SourceArtworkMeta } from './artwork-conversion-status';

export type PreparedEditorArtwork =
  | { kind: 'passthrough'; file: File }
  | { kind: 'pdf'; file: File; sourceArtwork?: SourceArtworkMeta }
  | { kind: 'needs-conversion'; previewUrl: string | null; sourceArtwork: SourceArtworkMeta };

export type ArtworkCopy = { failed: string; invalid: string };

async function requestConversion(upload: RegisteredUpload): Promise<{ status: 'converted'; upload: RegisteredUpload } | { status: 'needs-conversion'; previewUrl: string | null }> {
  try {
    const response = await fetch('/api/artwork/convert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: upload.url, proof: upload.proof }),
    });
    const data = await response.json().catch(() => null);
    if (response.ok && data?.status === 'converted' && data.upload?.url) return { status: 'converted', upload: data.upload };
    if (response.ok && data?.status === 'needs-conversion') {
      return { status: 'needs-conversion', previewUrl: typeof data.previewUrl === 'string' ? data.previewUrl : null };
    }
  } catch {
    // Valt hieronder terug op handmatige omzetting.
  }
  return { status: 'needs-conversion', previewUrl: null };
}

/**
 * Uploadt een PostScript-bestand en probeert het om te zetten. Gedeeld door de
 * ontwerpmodule en de gewone uploader, zodat beide hetzelfde besluiten.
 */
export async function uploadAndConvertPostScript(file: File, options: {
  copy: ArtworkCopy;
  productId?: string;
  providerType?: string;
  editorSessionId?: string;
  onProgress?: Parameters<typeof uploadFileToStorage>[1]['onProgress'];
}): Promise<{ original: RegisteredUpload; converted: RegisteredUpload | null; previewUrl: string | null }> {
  const original = await uploadFileToStorage(file, {
    requireProof: true,
    productId: options.productId,
    providerType: options.providerType,
    editorSessionId: options.editorSessionId,
    failureMessage: options.copy.failed,
    invalidFileMessage: options.copy.invalid,
    step: 'file-upload',
    onProgress: options.onProgress,
  });
  const result = await requestConversion(original);
  if (result.status === 'converted') return { original, converted: result.upload, previewUrl: null };
  return { original, converted: null, previewUrl: result.previewUrl };
}

export async function prepareEditorArtwork(file: File, options: {
  copy: ArtworkCopy;
  productId?: string;
  providerType?: string;
  editorSessionId?: string;
}): Promise<PreparedEditorArtwork> {
  const { file: prepared, detection } = await prepareArtworkFile(file, options.copy.invalid);
  if (!detection) return { kind: 'passthrough', file };
  if (detection.format === 'ai-pdf') return { kind: 'pdf', file: prepared };
  if (detection.format === 'svg') return { kind: 'passthrough', file: prepared };
  return convertForEditor(prepared, detection, options);
}

async function convertForEditor(file: File, detection: ArtworkDetection, options: {
  copy: ArtworkCopy;
  productId?: string;
  providerType?: string;
  editorSessionId?: string;
}): Promise<PreparedEditorArtwork> {
  const { original, converted, previewUrl } = await uploadAndConvertPostScript(file, options);
  const base: SourceArtworkMeta = { uploadId: original.id, url: original.url, fileName: original.fileName, mimeType: detection.mimeType || POSTSCRIPT_MIME, needsConversion: true };
  if (converted) {
    const response = await fetch(converted.url, { cache: 'no-store' });
    if (response.ok) {
      const blob = await response.blob();
      return {
        kind: 'pdf',
        file: new window.File([blob], converted.fileName, { type: 'application/pdf' }),
        sourceArtwork: { ...base, needsConversion: false, convertedPdfUrl: converted.url },
      };
    }
  }
  return { kind: 'needs-conversion', previewUrl, sourceArtwork: base };
}

/**
 * Neutrale plaatshouder voor een vectorbestand zonder voorbeeld: een kader met
 * de bestandsnaam, zodat de klant de plek en grootte van zijn logo kan kiezen.
 */
export function artworkPlaceholderDataUrl(fileName: string): string {
  const safe = String(fileName || 'logo').replace(/[<>&"']/g, '').slice(0, 60);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="400" viewBox="0 0 800 400">`
    + `<rect x="4" y="4" width="792" height="392" rx="16" fill="#f8fafc" stroke="#64748b" stroke-width="6" stroke-dasharray="24 14"/>`
    + `<path d="M330 90h100l40 40v120h-140z" fill="#e2e8f0" stroke="#475569" stroke-width="6"/>`
    + `<path d="M430 90v40h40" fill="none" stroke="#475569" stroke-width="6"/>`
    + `<text x="400" y="320" font-family="Arial, sans-serif" font-size="34" fill="#334155" text-anchor="middle">${safe}</text>`
    + `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
